import { useState } from "react";
import { X } from "lucide-react";

const empty = {
  immatriculation: "",
  conducteur: "",
  date_premiere_circulation: "",
  numero_licence: "",
  kilometrage: 0,
  geoloc_equipe: false,
  geoloc_modele: "",
  geoloc_position: "",
  latitude: "",
  longitude: "",
  motorisation: "",
  binome_debut: "",
  binome_fin: "",
  binome_suivi: "",
  controle_6mois_date: "",
  controle_annuel_date: "",
  vidange_km_dernier: 0,
  statut: "disponible",
  carte_type: "aucune",
  carte_numero: "",
  carte_conducteur: "",
  conso_reference: "",
};

const Field = ({ label, children }) => (
  <div>
    <label className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-1 block">{label}</label>
    {children}
  </div>
);

export const VehicleForm = ({ initial, onSubmit, onClose }) => {
  const [form, setForm] = useState({ ...empty, ...(initial || {}) });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSubmit({
        ...form,
        kilometrage: Number(form.kilometrage) || 0,
        vidange_km_dernier: Number(form.vidange_km_dernier) || 0,
        latitude: form.latitude === "" || form.latitude === null ? null : Number(form.latitude),
        longitude: form.longitude === "" || form.longitude === null ? null : Number(form.longitude),
        conso_reference: form.conso_reference === "" || form.conso_reference === null ? null : Number(form.conso_reference),
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "nb-input w-full px-3 py-2.5 font-medium text-sm";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
      <div className="nb-card w-full max-w-2xl max-h-[90vh] overflow-y-auto mc-scroll p-6 mc-pop" data-testid="vehicle-form">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-heading font-black text-2xl">
            {initial ? "Modifier le véhicule" : "Nouveau véhicule"}
          </h2>
          <button onClick={onClose} data-testid="form-close" className="w-9 h-9 rounded-lg nb-btn bg-white flex items-center justify-center">
            <X strokeWidth={2.5} size={18} />
          </button>
        </div>

        <form onSubmit={submit} className="space-y-5">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Immatriculation *">
              <input required data-testid="form-immatriculation" className={`${inputCls} font-mono-plate uppercase`} value={form.immatriculation} onChange={(e) => set("immatriculation", e.target.value)} placeholder="AB-123-CD" />
            </Field>
            <Field label="Conducteur">
              <input data-testid="form-conducteur" className={inputCls} value={form.conducteur} onChange={(e) => set("conducteur", e.target.value)} placeholder="Nom du conducteur" />
            </Field>
            <Field label="1ère mise en circulation">
              <input type="date" data-testid="form-date-circulation" className={inputCls} value={form.date_premiere_circulation} onChange={(e) => set("date_premiere_circulation", e.target.value)} />
            </Field>
            <Field label="N° licence">
              <input data-testid="form-licence" className={inputCls} value={form.numero_licence} onChange={(e) => set("numero_licence", e.target.value)} placeholder="Numéro de licence" />
            </Field>
            <Field label="Kilométrage">
              <input type="number" data-testid="form-kilometrage" className={inputCls} value={form.kilometrage} onChange={(e) => set("kilometrage", e.target.value)} />
            </Field>
            <Field label="Motorisation">
              <input data-testid="form-motorisation" className={inputCls} value={form.motorisation} onChange={(e) => set("motorisation", e.target.value)} placeholder="Diesel, Essence, Électrique..." />
            </Field>
          </div>

          <div className="border-t-2 border-slate-200 pt-4">
            <p className="text-xs font-bold uppercase tracking-wider text-[#4F46E5] mb-3">Géolocalisation</p>
            <div className="grid sm:grid-cols-3 gap-4 items-end">
              <label className="flex items-center gap-2 font-bold text-sm cursor-pointer">
                <input type="checkbox" data-testid="form-geoloc-equipe" checked={form.geoloc_equipe} onChange={(e) => set("geoloc_equipe", e.target.checked)} className="w-5 h-5 border-2 border-slate-900 rounded accent-[#4F46E5]" />
                Équipé GPS
              </label>
              <Field label="Modèle du boîtier">
                <input data-testid="form-geoloc-modele" disabled={!form.geoloc_equipe} className={`${inputCls} disabled:opacity-40`} value={form.geoloc_modele} onChange={(e) => set("geoloc_modele", e.target.value)} placeholder="Modèle GPS" />
              </Field>
              <Field label="Dernière position">
                <input data-testid="form-geoloc-position" disabled={!form.geoloc_equipe} className={`${inputCls} disabled:opacity-40`} value={form.geoloc_position} onChange={(e) => set("geoloc_position", e.target.value)} placeholder="Ville / coordonnées" />
              </Field>
            </div>
            <div className="grid sm:grid-cols-2 gap-4 mt-4">
              <Field label="Latitude">
                <input type="number" step="any" data-testid="form-latitude" disabled={!form.geoloc_equipe} className={`${inputCls} disabled:opacity-40`} value={form.latitude ?? ""} onChange={(e) => set("latitude", e.target.value)} placeholder="48.8566" />
              </Field>
              <Field label="Longitude">
                <input type="number" step="any" data-testid="form-longitude" disabled={!form.geoloc_equipe} className={`${inputCls} disabled:opacity-40`} value={form.longitude ?? ""} onChange={(e) => set("longitude", e.target.value)} placeholder="2.3522" />
              </Field>
            </div>
          </div>

          <div className="border-t-2 border-slate-200 pt-4">
            <p className="text-xs font-bold uppercase tracking-wider text-[#4F46E5] mb-3">Carte carburant / recharge</p>
            <div className="grid sm:grid-cols-3 gap-4">
              <Field label="Type de carte">
                <select data-testid="form-carte-type" className={inputCls} value={form.carte_type} onChange={(e) => set("carte_type", e.target.value)}>
                  <option value="aucune">Aucune</option>
                  <option value="gasoil">Gasoil</option>
                  <option value="electrique">Électrique</option>
                  <option value="les_deux">Gasoil + Électrique</option>
                </select>
              </Field>
              <Field label="N° de carte">
                <input data-testid="form-carte-numero" className={inputCls} value={form.carte_numero} onChange={(e) => set("carte_numero", e.target.value)} placeholder="N° carte" />
              </Field>
              <Field label="Conducteur titulaire">
                <input data-testid="form-carte-conducteur" className={inputCls} value={form.carte_conducteur} onChange={(e) => set("carte_conducteur", e.target.value)} placeholder="Nom du titulaire" />
              </Field>
            </div>
          </div>

          <div className="border-t-2 border-slate-200 pt-4">
            <p className="text-xs font-bold uppercase tracking-wider text-[#4F46E5] mb-3">Contrôles techniques</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Prochain CT (6 mois)">
                <input type="date" data-testid="form-ct-6mois" className={inputCls} value={form.controle_6mois_date} onChange={(e) => set("controle_6mois_date", e.target.value)} />
              </Field>
              <Field label="Prochain CT (annuel)">
                <input type="date" data-testid="form-ct-annuel" className={inputCls} value={form.controle_annuel_date} onChange={(e) => set("controle_annuel_date", e.target.value)} />
              </Field>
            </div>
          </div>

          <div className="border-t-2 border-slate-200 pt-4">
            <p className="text-xs font-bold uppercase tracking-wider text-[#4F46E5] mb-3">Binôme</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Début binôme">
                <input type="date" data-testid="form-binome-debut" className={inputCls} value={form.binome_debut} onChange={(e) => set("binome_debut", e.target.value)} />
              </Field>
              <Field label="Fin binôme">
                <input type="date" data-testid="form-binome-fin" className={inputCls} value={form.binome_fin} onChange={(e) => set("binome_fin", e.target.value)} />
              </Field>
            </div>
            <div className="mt-4">
              <Field label="Suivi du binôme">
                <textarea data-testid="form-binome-suivi" className={`${inputCls} resize-none`} rows={2} value={form.binome_suivi} onChange={(e) => set("binome_suivi", e.target.value)} placeholder="Notes de suivi du binôme..." />
              </Field>
            </div>
          </div>

          <div className="border-t-2 border-slate-200 pt-4">
            <p className="text-xs font-bold uppercase tracking-wider text-[#4F46E5] mb-3">Vidange</p>
            <Field label="Km de la dernière vidange (prochaine à +30 000 km)">
              <input type="number" data-testid="form-vidange-km" className={inputCls} value={form.vidange_km_dernier} onChange={(e) => set("vidange_km_dernier", e.target.value)} />
            </Field>
          </div>

          <div className="border-t-2 border-slate-200 pt-4">
            <p className="text-xs font-bold uppercase tracking-wider text-[#4F46E5] mb-3">Consommation de référence</p>
            <Field label="Conso. de référence (L/100 km) — pour détecter la surconsommation">
              <input type="number" step="any" data-testid="form-conso-reference" className={inputCls} value={form.conso_reference ?? ""} onChange={(e) => set("conso_reference", e.target.value)} placeholder="ex: 6.5" />
            </Field>
          </div>

          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={saving} data-testid="form-submit" className="flex-1 bg-[#4F46E5] text-white font-bold py-3 rounded-xl nb-btn disabled:opacity-60">
              {saving ? "Enregistrement..." : "Enregistrer"}
            </button>
            <button type="button" onClick={onClose} className="px-6 bg-white font-bold py-3 rounded-xl nb-btn">
              Annuler
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
